import { AppDispatch } from "../core/data/redux/store"
import { setSystems } from "../core/data/redux/slice/systemsSlice"
import { setSessions } from "../core/data/redux/slice/sessionsSlice"
import { Session } from "../service/sessions"
import { setBranches, setCurrentBranch, Branch } from "../core/data/redux/slice/branchesSlice"
import { getAllSystems } from "../service/systems"
import { getAllSessions } from "../service/sessions"
import { certBranchesApi } from "../service/certificate"
import { isSuperAdmin } from "./rbacHelper"

interface BootstrapUser {
  _id: string
  branch?: { _id: string } | string
  accessibleBranches?: ({ _id: string } | string)[]
  role?: { name?: string }
}

export interface BootstrapResult {
  sessions: Session[]
}

// Show only the campuses this user may actually use: super-admin
// (owner/admin) sees all; everyone else sees their home branch +
// accessibleBranches. The backend already enforces access on every
// request — this just stops the switcher from listing campuses the user
// can't use (which silently fell back to their home campus anyway).
//
// Also pins the active campus to THIS user's home campus, unless they have a
// still-valid selection of their own. Prevents a stale/cross-user
// localStorage selection (or branches[0]) from showing the wrong campus's
// logo + systems — each user reliably lands on their own campus.
const applyBranches = (dispatch: AppDispatch, userData: BootstrapUser, rawBranches: Branch[]): Branch[] => {
  const allowedIds = new Set(
    [userData.branch, ...(userData.accessibleBranches || [])]
      .filter((b): b is { _id: string } | string => Boolean(b))
      .map((b) => String(typeof b === "string" ? b : b._id))
  )
  const branches = isSuperAdmin(userData)
    ? rawBranches
    : rawBranches.filter((b) => allowedIds.has(String(b._id)))
  dispatch(setBranches(branches))

  const homeId = String(
    (typeof userData.branch === "string" ? userData.branch : userData.branch?._id) || ""
  )
  let persisted: string | null = null
  try {
    persisted = localStorage.getItem("currentBranchId")
  } catch {
    /* ignore */
  }
  const persistedValid =
    persisted && persisted !== "all" && branches.some((b) => String(b._id) === persisted)
  if (!persistedValid) {
    const home = branches.find((b) => String(b._id) === homeId) || branches[0]
    if (home) dispatch(setCurrentBranch(home))
  }

  return branches
}

// The branches endpoint is slow (it mints S3 logo URLs per campus) and
// sometimes times out. If the first attempt (in loadBootstrapData) fails,
// keep retrying a few times in the background — deliberately NOT awaited by
// the caller, so the app unblocks and the user gets in immediately, and the
// campus dropdown/logo self-heal a few seconds later without needing a
// manual reload.
const retryBranchesInBackground = async (
  dispatch: AppDispatch,
  userData: BootstrapUser,
  attempts = 3,
  delayMs = 4000
): Promise<void> => {
  for (let i = 0; i < attempts; i++) {
    await new Promise((resolve) => setTimeout(resolve, delayMs))
    try {
      const res = await certBranchesApi()
      const fresh = res?.data || []
      if (fresh.length) {
        applyBranches(dispatch, userData, fresh)
        return
      }
    } catch {
      /* keep retrying */
    }
  }
}

// Loads everything the app needs right after authenticating — systems,
// sessions, and branches — and dispatches it to Redux. Shared by the login
// flow and the page-load/refresh flow so they can't silently drift apart
// (this used to be duplicated, and login's copy was missing branches
// entirely, which is why the campus dropdown/logo were absent right after
// logging in until the next full page refresh).
//
// Each fetch fails independently (Promise.allSettled, not Promise.all) —
// one of them being down must never block or invalidate the others, and
// must never be treated as an auth failure (that's the caller's job, based
// on whether the user's own identity fetch/login succeeded — not this
// supplementary data).
export const loadBootstrapData = async (
  dispatch: AppDispatch,
  userData: BootstrapUser
): Promise<BootstrapResult> => {
  const [systemsResult, sessionsResult, branchesResult] = await Promise.allSettled([
    getAllSystems(),
    getAllSessions({ limit: 1000 }),
    certBranchesApi(),
  ])

  if (systemsResult.status === "fulfilled") {
    dispatch(setSystems(systemsResult.value.data))
  } else {
    console.error("Failed to load systems:", systemsResult.reason)
  }

  let sessions: Session[] = []
  if (sessionsResult.status === "fulfilled") {
    sessions = sessionsResult.value.sessions || []
    // setSessions restores the user's last-picked session (persisted in
    // localStorage) or falls back to the configured default — never
    // overridden here, or every login/reload would discard the user's choice.
    dispatch(setSessions(sessions))
  } else {
    console.error("Failed to load sessions:", sessionsResult.reason)
  }

  if (branchesResult.status === "fulfilled") {
    applyBranches(dispatch, userData, branchesResult.value.data || [])
  } else {
    console.error("Failed to load branches:", branchesResult.reason)
    // Don't block the app on this — the user's already in by this point.
    // Keep trying quietly so the dropdown/logo self-heal without a reload.
    retryBranchesInBackground(dispatch, userData)
  }

  return { sessions }
}
